import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Lightbulb, BarChart3, Award, Users, CheckCircle, Compass } from 'lucide-react';

const TEAM_IMAGE = 'https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZyUyMGRpdmVyc2UlMjBleGVjdXRpdmV8ZW58MHx8fHwxNzcwODk0MTEzfDA&ixlib=rb-4.1.0&q=85';
const ABSTRACT_IMAGE = 'https://images.unsplash.com/photo-1767658647133-2168a9cfc1d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHN0cnVjdHVyZSUyMGdyZXklMjB3aGl0ZSUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzcwODk0MTIxfDA&ixlib=rb-4.1.0&q=85';

const AboutPage = () => {
  const { t } = useLanguage();

  const methodologySteps = [
    {
      icon: Compass,
      title: 'Team Dynamics Assessment',
      description: 'Diagnosing collaboration patterns, leadership dynamics, and delivery blockers through structured interviews, observation, and tools inspired by Agile Ways of Working.'
    },
    {
      icon: Lightbulb,
      title: 'Tailor-Made Program Design',
      description: 'Creating custom programs based on assessment, combining recruitment, leadership coaching, and hands-on team support.'
    },
    {
      icon: BarChart3,
      title: 'Engagement Tracking & Reporting',
      description: 'Monitoring key indicators (engagement, cohesion, accountability, retention risk) and providing clear reporting for measurable progress.'
    }
  ];

  const certifications = [
    { 
      name: 'LeSS', 
      description: 'Certified Practitioner',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '0% 0%',
      size: '20%'
    },
    { 
      name: 'SAFe', 
      description: 'Scaled Agile Framework',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '25% 0%',
      size: '20%'
    },
    { 
      name: 'TKI', 
      description: 'Conflict Mode Instrument',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '50% 0%',
      size: '20%'
    },
    { 
      name: 'PCM', 
      description: 'Process Communication Model',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '70% 0%',
      size: '20%'
    },
    { 
      name: 'MBTI', 
      description: 'Myers-Briggs Certified',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '90% 0%',
      size: '20%'
    },
    { 
      name: 'NLP', 
      description: 'Neuro-Linguistic Programming',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '0% 100%',
      size: '20%'
    },
    { 
      name: 'Gallup', 
      description: 'Certified Strengths Coach',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '35% 100%',
      size: '20%'
    },
    { 
      name: 'Prosci', 
      description: 'Certified Change Practitioner',
      logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/8szgo42v_image.png',
      position: '70% 100%',
      size: '20%'
    }
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#1A1A1A] text-white" data-testid="about-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-6">
                {t('about.title')}
              </p>
              <h1 className="text-5xl md:text-6xl font-bold leading-[1.1] mb-6">
                Sustainable Execution Architects
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                {t('about.approachText')}
              </p>
              <Link to="/diagnosis">
                <Button className="btn-bronze flex items-center gap-2" data-testid="about-cta-btn">
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src={TEAM_IMAGE} 
                alt="TeamCraft team"
                className="rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-white" data-testid="vision-mission">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="border-l-4 border-[#C5A059] pl-8">
              <h2 className="text-3xl font-semibold text-[#1A1A1A] mb-4">{t('about.vision')}</h2>
              <p className="text-xl text-gray-600 leading-relaxed italic">
                "{t('about.visionText')}"
              </p>
            </div>
            <div className="border-l-4 border-[#1A1A1A] pl-8">
              <h2 className="text-3xl font-semibold text-[#1A1A1A] mb-4">{t('about.mission')}</h2>
              <p 
                className="text-xl text-gray-600 leading-relaxed tracking-wide"
                dangerouslySetInnerHTML={{ __html: t('about.missionText') }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="py-24 section-alt" data-testid="differentiators">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-4">
              What Sets Us Apart
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A]">
              Not Just Recruiters. Not Just Coaches.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Performance Partners',
                description: 'We work at the intersection where hiring, leadership, and culture converge to drive sustainable execution.'
              },
              {
                icon: Users,
                title: 'Luxembourg Expertise',
                description: 'Deep understanding of the Luxembourg market, its unique business culture, and talent landscape.'
              },
              {
                icon: BarChart3,
                title: 'Measurable Impact',
                description: 'Every engagement includes tracking, reporting, and continuous improvement mechanisms.'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-8 border border-gray-200 card-hover">
                <div className="w-14 h-14 bg-[#1A1A1A] flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section 
        className="py-24 bg-white relative"
        style={{
          backgroundImage: `url(${ABSTRACT_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay'
        }}
        data-testid="methodology-section"
      >
        <div className="absolute inset-0 bg-white/95"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-4">
              {t('about.methodology')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A]">
              Our Structured Approach
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {methodologySteps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-6xl font-bold text-[#C5A059]/20 font-mono">0{idx + 1}</span>
                </div>
                <div className="w-12 h-12 bg-[#C5A059] flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-[#1A1A1A]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-[#1A1A1A] text-white" data-testid="certifications-detail">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-4">
              {t('about.certifications')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold">
              Our Tools & Certifications
            </h2>
            <p className="text-gray-400 mt-4">
              Industry-recognized frameworks and methodologies we leverage for sustainable results
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: 'LeSS', desc: 'Certified Practitioner', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/c68od8uh_Less%20certified.png' },
              { name: 'SAFe', desc: 'Scaled Agile Framework', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/zycy1zj3_Safe%20Certified.png' },
              { name: 'TKI', desc: 'Conflict Mode Instrument', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/6ygieiqm_TKI.png' },
              { name: 'PCM', desc: 'Process Communication', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/0y90nuj4_PCM.png' },
              { name: 'MBTI', desc: 'Myers-Briggs Certified', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/gi19s4uw_MBTI.png' },
              { name: 'NLP', desc: 'Neuro-Linguistic Programming', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/5khbpxnk_NLP.jpg' },
              { name: 'Gallup', desc: 'Certified Strengths Coach', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/w9z58jx6_Gallup.png' },
              { name: 'Prosci', desc: 'Change Practitioner', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/l1jzaol8_prosci.png' }
            ].map((cert, idx) => (
              <div key={idx} className="flex flex-col items-center p-5 bg-white rounded-lg shadow-sm">
                <div className="h-20 w-full flex items-center justify-center mb-3">
                  <img 
                    src={cert.logo} 
                    alt={cert.name}
                    className="max-h-16 max-w-full object-contain"
                  />
                </div>
                <h4 className="font-semibold text-[#1A1A1A] text-base">{cert.name}</h4>
                <p className="text-gray-500 text-xs text-center">{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white" data-testid="about-cta-section">
        <div className="container-custom text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A] mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Start with our free Team Performance Diagnosis to identify your team's strengths and areas for improvement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/diagnosis">
                <Button className="btn-primary text-lg px-10 py-6 flex items-center gap-2" data-testid="about-final-cta">
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="btn-secondary text-lg px-10 py-6" data-testid="about-services-btn">
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
